'use client';

import React from 'react';
import { PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import { resolveReference } from '@/utils/lib/resolve-reference';
import { Reference } from '@/types/lib/reference';
import { FooterProps } from './types';
import useClassNames from './hooks/useClassNames';

const linkClassName = 'text-14 text-white/80 transition-colors hover:text-white';

// resolveReference() returns href '/' for an empty reference, so detect a real
// destination on the raw reference — never on the resolved href.
const hasReference = (ref?: Reference): boolean =>
  (ref?.type === 'link' && !!ref.link) || (ref?.type === 'page-folder' && !!ref.pageFolder?._url);

const Footer = ({
  logo,
  companyBlurb = '',
  copyrightStatement = '',
  linkGroups = [],
  phone = '',
  email = '',
  officeHoursLabel = '',
  officeHoursValue = '',
  privacyLabel = '',
  privacyHref,
  termsLabel = '',
  termsHref,
  paymentIcons = [],
  variant = 'default',
}: FooterProps) => {
  const { className } = useClassNames(variant);
  const translate = useTranslations('footer');

  // The year is always injected in code (via the {year} token) so it never goes
  // stale (EWM-297 dynamic-year AC). No i18n fallback — empty Studio value hides.
  const year = new Date().getFullYear();
  const copyrightText = copyrightStatement ? copyrightStatement.replace('{year}', String(year)) : '';

  if (variant === 'simple') {
    return (
      <footer className={className}>
        {copyrightText && (
          <div className="text-12 md:text-14 cursor-default text-center text-white">{copyrightText}</div>
        )}
      </footer>
    );
  }

  const hasLogo = !!(logo?.media?.mediaId || logo?.media?.file || logo?.src);
  const hasContact = !!(phone || email || officeHoursLabel || officeHoursValue);

  const legalLinks = [
    { label: privacyLabel, href: privacyHref },
    { label: termsLabel, href: termsHref },
  ].filter((l) => l.label && hasReference(l.href));

  const contactDetails = (
    <div className="space-y-3">
      {phone && (
        <div className="flex items-center gap-2">
          <PhoneIcon aria-hidden className="h-4 w-4 text-white/60" />
          <a
            href={`tel:${phone.replace(/\s/g, '')}`}
            className="text-14 font-semibold text-white transition-colors hover:text-white/80"
          >
            {phone}
          </a>
        </div>
      )}
      {email && (
        <div className="flex items-center gap-2">
          <EnvelopeIcon aria-hidden className="h-4 w-4 text-white/60" />
          <a href={`mailto:${email}`} className={linkClassName}>
            {email}
          </a>
        </div>
      )}
      {(officeHoursLabel || officeHoursValue) && (
        <div className="text-13 pt-1 text-white/70">
          {officeHoursLabel && <p className="font-medium text-white/80">{officeHoursLabel}</p>}
          {officeHoursValue && <p>{officeHoursValue}</p>}
        </div>
      )}
    </div>
  );

  return (
    <footer className={className}>
      <div className="mx-auto max-w-7xl px-4">
        {/* Main footer content */}
        <div className="grid grid-cols-1 gap-8 py-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Logo and about */}
          <div className="lg:col-span-1">
            {hasLogo && (
              <Link href="/" underlineOnHover={false} className="mb-4 inline-block">
                <Image
                  media={logo?.media ?? {}}
                  src={logo?.media?.file ?? logo?.src ?? ''}
                  alt={translate('logo-alt')}
                  width={logo?.width ?? (Number(logo?.media?.width) || 160)}
                  height={logo?.height ?? (Number(logo?.media?.height) || 40)}
                  className="h-10 w-auto"
                />
              </Link>
            )}
            {companyBlurb && <p className="text-14 leading-relaxed text-white/80">{companyBlurb}</p>}
          </div>

          {/* Empty gap column (desktop) */}
          <div className="hidden lg:block" />

          {/* Link groups (Ordering, Company, …) — contact block sits under the last group */}
          {linkGroups.map((group, gi) => {
            const groupLinks = (group.links ?? []).filter((l) => l.label && hasReference(l.href));
            const showContactHere = hasContact && gi === linkGroups.length - 1;
            // Render a group only when it has visible links or hosts the contact
            // block — a title alone (all links filtered out) must not leave a bare
            // heading in an empty column.
            if (!groupLinks.length && !showContactHere) return null;
            return (
              <div key={group.title || gi}>
                {group.title && <h3 className="text-16 mb-4 font-bold text-white">{group.title}</h3>}
                {groupLinks.length > 0 && (
                  <ul className={showContactHere ? 'mb-6 space-y-2' : 'space-y-2'}>
                    {groupLinks.map((l, li) => (
                      <li key={l.label || li}>
                        <Link
                          href={resolveReference(l.href).href ?? '/'}
                          underlineOnHover={false}
                          className={linkClassName}
                        >
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                {showContactHere && contactDetails}
              </div>
            );
          })}

          {/* Contact block as its own column when there are no link groups */}
          {hasContact && linkGroups.length === 0 && <div>{contactDetails}</div>}
        </div>

        {/* Bottom bar */}
        <div className="mt-4 border-t border-white/20 pt-4 pb-1">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {copyrightText && <p className="text-13 text-white/70">{copyrightText}</p>}

            {(legalLinks.length > 0 || paymentIcons.length > 0) && (
              <div className="flex items-center gap-6">
                {legalLinks.map((l, i) => (
                  <Link
                    key={l.label || i}
                    href={resolveReference(l.href).href ?? '/'}
                    underlineOnHover={false}
                    className="text-13 text-white/70 transition-colors hover:text-white"
                  >
                    {l.label}
                  </Link>
                ))}

                {paymentIcons.length > 0 && (
                  <div
                    role="img"
                    aria-label={translate('payment-methods-label')}
                    className="ml-2 flex items-center gap-2"
                  >
                    {paymentIcons.map((p, i) => (
                      <div key={p.alt || i} className="flex items-center justify-center rounded bg-white px-1.5 py-1">
                        <Image
                          media={p.icon?.media ?? {}}
                          src={p.icon?.media?.file ?? p.icon?.src ?? ''}
                          alt={p.alt ?? ''}
                          width={p.icon?.width ?? (Number(p.icon?.media?.width) || 40)}
                          height={p.icon?.height ?? (Number(p.icon?.media?.height) || 16)}
                          className="h-3.5 w-auto"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
